import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Stack, Typography, useTheme } from "@mui/material";
import FormProviderComponent from '../../components/form/formProviderComponent';
import { encrypt, validateSriLankanMobileNumber } from '../../utils/customFunctions';
import { ButtonComponent, TextFieldComponent } from '../../components/form';
import SnackbarComponent from '../../components/snackBar/snackbarComponent';
import { useEffect, useState } from 'react';
import { useRequestOtpMutation, useHasAccessMutation } from '../../services/authApi';

const LoginView = ({
    contactAdmin,
    showOtpView
}) => {
    const theme = useTheme();

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const formSchema = yup.object().shape({
        mobile: yup.number().required('Mobile number is required').typeError('Invalid mobile number').test('isValidMobile', 'Invalid mobile number', () => {
            const isValid = IsUsernameValid();
            return isValid;
        })
    });

    const defaultValues = {
        mobile: ''
    };

    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });

    const {
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;

    const values = watch();

    const [
        requestOtp, 
        {
          isLoading: isLoadingRequestOtp,
          isSuccess: isSuccessRequestOtp,
          isError: isErrorRequestOtp,
          error: errorRequestOtp
        }
    ] = useRequestOtpMutation();

    useEffect(() => {
        if(isSuccessRequestOtp){
            console.log("Otp sent to the mobile...");

            setSnackBar({
                message: "We've sent an otp to your mobile",
                color: "green",
                open: true
            });

            const authObj = {
                'mobile': `+94${values.mobile}`,
                'time': new Date().getTime()
            }

            const encryptedData = encrypt(authObj);

            sessionStorage.setItem('authSession', encryptedData);

            setTimeout(() => {
                showOtpView();
            }, 2000);
        }
    }, [isSuccessRequestOtp]);

    useEffect(() => {
        if(isErrorRequestOtp && errorRequestOtp){
            console.log('OTP error:', errorRequestOtp);

            const message =
                errorRequestOtp?.data?.message ||
                errorRequestOtp?.data?.error?.message ||
                errorRequestOtp?.error ||
                'Failed to send OTP';

            setSnackBar({
                message: message,
                color: "red",
                open: true
            });
        }
    }, [isErrorRequestOtp, errorRequestOtp]);

    const [
        checkAccess, 
        {
          isLoading: isLoadingCheckAccess,
          isSuccess: isSuccessCheckAccess,
          isError: isErrorCheckAccess,
          data: dataCheckAccess,
          error: errorCheckAccess
        }
    ] = useHasAccessMutation();

    useEffect(() => {
        if(isSuccessCheckAccess && dataCheckAccess?.hasAccess){
            console.log('Access granted');
            requestOtp({mobileNo: `+94${values.mobile}`});
        }
    }, [isSuccessCheckAccess, dataCheckAccess]);

    useEffect(() => {
        if(isErrorCheckAccess || dataCheckAccess?.hasAccess === false){
            console.log('Access denied:', errorCheckAccess);

            setSnackBar({
                message: "You don't have access to this application",
                color: "red",
                open: true
            });
        }
    }, [isErrorCheckAccess, errorCheckAccess, dataCheckAccess]);

    const IsUsernameValid = () => {
        return validateSriLankanMobileNumber(`+94${values.mobile}`);
    }

    const onSubmit = handleSubmit(async(data) => {
        console.log(data);

        try{
            checkAccess({mobileNo: `+94${data.mobile}`});
        }
        catch(error){
            setSnackBar({
                message: {error},
                color: "red",
                open: true
            });
        }
    });

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='center'
                spacing={5}
                width='100%'
                height='100vh'
            >
                {/* Logo */}
                <Box
                    sx={{
                        width: {xs: 100, md: 150},
                        height: {xs: 100, md: 150}
                    }}
                >
                    <img 
                        src="/logo512.png" 
                        alt="logo"
                        width='100%'
                    />
                </Box>

                {/* Titles */}
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                >
                    {/* Title */}
                    <Stack
                        direction='row'
                        justifyContent='center'
                        alignItems='center'
                    >
                        {/* Digital */}
                        <Box
                            sx={{
                                width: {xs: 93, md: 154},
                                height: {xs: 33, md: 50},
                                mr: 2
                            }}
                        >
                            <img 
                                src="/images/digital.png" 
                                alt="logo"
                                width='100%'
                            />
                        </Box>

                        {/* Tourism */}
                        <Box
                            sx={{
                                width: {xs: 108, md: 178},
                                height: {xs: 25, md: 37},
                                mt: -1.5
                            }}
                        >
                            <img 
                                src="/images/tourism.png" 
                                alt="logo"
                                width='100%'
                            />
                        </Box>
                    </Stack>

                    {/* Sub title */}
                    <Typography
                        fontWeight={400}
                        fontSize={{xs: 14, md: 20}}
                        letterSpacing={0.16}
                        color="rgba(128, 138, 147, 1)"
                    >
                        Srilanka driver guide
                    </Typography>
                </Stack>

                {/* Form */}
                <FormProviderComponent methods={formMethods} onSubmit={onSubmit}>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <TextFieldComponent 
                            name='mobile'
                            placeholder='Mobile Number'
                            fullWidth
                            prefix={
                                <Stack
                                    direction='row'
                                    justifyContent='start'
                                    alignItems='center'
                                >
                                    <img 
                                        src='/images/country.png' 
                                        alt='country'
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            paddingRight: '15px'
                                        }}
                                    />

                                    <Typography
                                        fontWeight={400}
                                        fontSize={16}
                                        letterSpacing={0.36}
                                        pr={1}
                                    >
                                        +94
                                    </Typography>
                                </Stack>
                            }
                        />

                        <ButtonComponent 
                            label='Login'
                            inLoading={isLoadingCheckAccess || isLoadingRequestOtp}
                            fullWidth
                            type="submit"
                        />
                    </Stack>
                </FormProviderComponent>
            </Stack>

            {/* Contact admin */}
            <ButtonComponent 
                variant="text"
                label={
                    <Typography
                        fontWeight={400}
                        fontSize={14}
                        color='#000000'
                    >
                        Contact <span style={{color: theme.palette.primary.main, fontWeight: 700}}>Admin</span>
                    </Typography>
                }
                sx={{
                    bottom: 0,
                    mb: 5,
                    bgcolor: 'transparent',
                    display: {xs: 'block', md: 'none'}
                }}
                disableRipple
                onClick={contactAdmin}
            />

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default LoginView;
